import { useEffect, useRef, useState } from 'react';
import { getQuestionAudioUrl } from '../lib/api';
import Button from './ui/Button';

interface QuestionCardProps {
  token: string;
  idx: number;
  /** Called when the audio finishes playing (so the parent can enable PTT). */
  onAudioEnded: () => void;
  onAudioStart?: () => void;
}

type PlaybackState = 'loading' | 'playing' | 'ended' | 'error';

/**
 * Streams the pre-generated question audio from the backend. The
 * <audio> element handles buffering/playback; we just wire its events to the
 * parent's phase machine and expose a replay button when it ends.
 */
export default function QuestionCard({
  token,
  idx,
  onAudioEnded,
  onAudioStart,
}: QuestionCardProps) {
  const [state, setState] = useState<PlaybackState>('loading');
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const onAudioEndedRef = useRef(onAudioEnded);
  const onAudioStartRef = useRef(onAudioStart);

  // Refs kept in sync so event handlers always see the latest callbacks
  // without re-triggering the auto-play effect below.
  useEffect(() => {
    onAudioEndedRef.current = onAudioEnded;
    onAudioStartRef.current = onAudioStart;
  });

  const src = getQuestionAudioUrl(token, idx);

  useEffect(() => {
    const el = audioRef.current;
    if (!el) return;
    setState('loading');
    el.currentTime = 0;
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => setState('error'));
    }
  }, [src]);

  function replay() {
    const el = audioRef.current;
    if (!el) return;
    el.currentTime = 0;
    setState('loading');
    const p = el.play();
    if (p && typeof p.catch === 'function') {
      p.catch(() => setState('error'));
    }
  }

  return (
    <article className="mb-6">
      <h2 className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        Question {idx}
      </h2>
      <div className="text-sm text-muted-foreground">
        <audio
          ref={audioRef}
          src={src}
          preload="auto"
          onPlay={() => {
            setState('playing');
            onAudioStartRef.current?.();
          }}
          onEnded={() => {
            setState('ended');
            onAudioEndedRef.current();
          }}
          onError={() => setState('error')}
        />
        {state === 'loading' && (
          <span className="inline-flex items-center gap-2 italic">
            Loading question audio…
          </span>
        )}
        {state === 'playing' && (
          <span className="inline-flex items-center gap-2 italic">
            Listening — please wait…
          </span>
        )}
        {state === 'ended' && (
          <Button variant="link" onClick={replay}>
            Replay question
          </Button>
        )}
        {state === 'error' && (
          <Button variant="ghost" onClick={replay}>
            Tap to play question
          </Button>
        )}
      </div>
    </article>
  );
}
